import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, User, ArrowRight, Menu } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { FEATURES, FEATURE_GROUPS as GROUPS, sellerCenterPath, useOpenFeature } from './mainContent';

export const MainLogo = ({ className = '' }) => (
  <Link to="/main-renewal" className={`font-black tracking-tight leading-none ${className}`} data-testid="main-logo">
    <span className="bg-gradient-to-r from-[#1E5BFF] to-[#4F46E5] bg-clip-text text-transparent">LIVE</span>
    <span className="text-[#1B2140]">MOA</span>
  </Link>
);


export const scrollToFeature = (key) => {
  document.getElementById(`main-feature-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const NavDropdown = ({ group }) => {
  const open = useOpenFeature();
  return (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger
      className="flex items-center gap-1.5 text-[17px] font-semibold text-[#1B2140] hover:text-[#2F5BFF] transition-colors outline-none"
      data-testid={`main-nav-${group.key}`}>
      {group.label} <ChevronDown className="w-4 h-4" />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="center" className="min-w-[240px] rounded-xl p-1.5">
      {FEATURES.filter((f) => f.group === group.key).map((f) => (
        <DropdownMenuItem key={f.key} onSelect={() => open(f)}
          className="rounded-lg py-2.5 cursor-pointer gap-2" data-testid={`main-nav-item-${f.key}`}>
          <f.icon className="w-4 h-4 text-[#2F5BFF]" />{f.title}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
  );
};

const LoginBtn = ({ small }) => (
  <Link to="/login" data-testid="main-login-btn"
    className={`inline-flex items-center gap-1.5 rounded-xl border border-[#C9CFEA] bg-white text-[#1B2140] font-semibold hover:border-[#2F5BFF] transition-colors ${small ? 'h-9 px-2.5 text-[13px]' : 'h-12 px-5 text-[16px]'}`}>
    <User className={small ? 'w-4 h-4' : 'w-5 h-5'} /> 로그인
  </Link>
);

const SellerBtn = ({ user, small }) => (
  <Link to={sellerCenterPath(user)} data-testid="main-seller-center-btn"
    className={`inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#1E5BFF] to-[#5B3DF5] text-white font-bold shadow-[0_6px_18px_rgba(47,91,255,0.35)] hover:brightness-110 transition-[filter] ${small ? 'h-9 px-2.5 text-[13px]' : 'h-12 px-6 text-[16px]'}`}>
    판매자센터 <ArrowRight className={small ? 'w-3.5 h-3.5' : 'w-5 h-5'} />
  </Link>
);

const MobileMenu = ({ open, setOpen, user }) => {
  const openFeature = useOpenFeature();
  return (
  <Sheet open={open} onOpenChange={setOpen}>
    <SheetContent side="right" className="w-[82vw] max-w-sm overflow-y-auto" data-testid="main-mobile-menu">
      <SheetHeader><SheetTitle className="text-left"><MainLogo className="text-2xl" /></SheetTitle></SheetHeader>
      <div className="mt-6 space-y-6">
        {GROUPS.map((g) => (
          <div key={g.key}>
            <p className="text-xs font-bold text-gray-400 mb-2">{g.label}</p>
            {FEATURES.filter((f) => f.group === g.key).map((f) => (
              <button key={f.key} type="button" data-testid={`main-mobile-menu-item-${f.key}`}
                onClick={() => { setOpen(false); openFeature(f); }}
                className="w-full flex items-center gap-2 text-left py-2.5 text-[15px] font-semibold text-[#1B2140]">
                <f.icon className="w-4 h-4 text-[#2F5BFF] shrink-0" />{f.title}
              </button>
            ))}
          </div>
        ))}
        <div className="flex gap-2 pt-2"><LoginBtn small /><SellerBtn user={user} small /></div>
      </div>
    </SheetContent>
  </Sheet>
  );
};

export const MainHeader = ({ user }) => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-black/5" data-testid="main-header">
      <div className="h-16 md:h-20 max-w-[1440px] mx-auto px-4 md:px-12 flex items-center justify-between">
        <MainLogo className="text-[26px] md:text-[38px]" />
        <nav className="hidden md:flex items-center gap-20">
          {GROUPS.map((g) => <NavDropdown key={g.key} group={g} />)}
        </nav>
        <div className="hidden md:flex items-center gap-4"><LoginBtn /><SellerBtn user={user} /></div>
        <div className="flex md:hidden items-center gap-1.5">
          <LoginBtn small /><SellerBtn user={user} small />
          <button type="button" aria-label="전체 메뉴" onClick={() => setOpen(true)}
            className="w-9 h-9 flex items-center justify-center text-[#1B2140]" data-testid="main-mobile-menu-btn">
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>
      <MobileMenu open={open} setOpen={setOpen} user={user} />
    </header>
  );
};
